import { useIntersectionObserver } from '@packages/shared/src/hooks/useIntersectionObserver/useIntersectionObserver';
import { useEffect, useRef, useState } from 'react';

export interface StickyProps {
  children: React.ReactNode;
  className?: string;
  onViewportChange?: (isInViewport: boolean) => void;
}

export const StickyInsideParent = ({
  children,
  className,
  onViewportChange,
  ...props
}: StickyProps) => {
  useEffect(() => {
    if (onViewportChange) {
      onViewportChange(true);
    }
  }, [onViewportChange]);

  return (
    <div className={`sticky top-0 z-10 ${className}`} {...props}>
      {children}
    </div>
  );
};

export const Sticky = ({ children, onViewportChange, className, ...props }: StickyProps) => {
  const stickyContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onViewportChange) {
      onViewportChange(false);
    }
  }, [onViewportChange]);

  useEffect(() => {
    const scrollTopButton = document.getElementById('scrollup-wrapper');

    if (scrollTopButton) {
      if (stickyContainerRef && stickyContainerRef.current) {
        scrollTopButton.style.transform = `translateY(-${stickyContainerRef.current.clientHeight}px)`;
      }
    }

    return () => {
      if (scrollTopButton) {
        scrollTopButton.style.transform = 'translateY(0)';
      }
    };
    // our Buybox height is dynamic when inside/outside the viewport
  }, [stickyContainerRef?.current?.clientHeight]);

  return (
    <div
      ref={stickyContainerRef}
      className={`fixed bottom-0 left-0 z-10 block w-full ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

export const StickyOutsideViewport = ({ children, className, onViewportChange }: StickyProps) => {
  const onSiteElementRef = useRef<HTMLDivElement>(null);
  // Debounce mechanism to prevent flickering (see ORDER-3788 for details)
  const [isStableIntersecting, setIsStableIntersecting] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { isIntersecting } = useIntersectionObserver(onSiteElementRef, {
    threshold: 0.1,
    intersectOnce: false,
  });

  useEffect(() => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only update the stable state after a delay to prevent flickering
    timeoutRef.current = setTimeout(() => {
      setIsStableIntersecting(isIntersecting);
    }, 150);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isIntersecting]);

  useEffect(() => {
    if (onViewportChange) {
      onViewportChange(isStableIntersecting);
    }
  }, [isStableIntersecting, onViewportChange]);

  return (
    <>
      <div ref={onSiteElementRef} className={className}>
        {children}
      </div>
      {!isStableIntersecting && <Sticky className={className}>{children}</Sticky>}
    </>
  );
};
